import { Component, ChangeDetectionStrategy, ViewEncapsulation, input, output, signal, viewChild, ElementRef, AfterViewInit, OnDestroy, inject, NgZone } from '@angular/core';

@Component({
  selector: 'tidy-tabs',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./tabs.component.scss'],
  template: `
    @if (showLeftArrow()) {
      <button class="tidy-tabs-arrow tidy-tabs-arrow-left" (click)="scrollLeft()" aria-label="Scroll tabs left">
        <span
          class="tidy-tabs-arrow-icon"
          [style.-webkit-mask-image]="'url(assets/svg/chevron-back-outline.svg)'"
          [style.mask-image]="'url(assets/svg/chevron-back-outline.svg)'"></span>
      </button>
    }
    <div class="tidy-tabs-scroll-container" #scrollContainer>
      @for (tab of tabs(); track tab.value) {
        <div
          class="tidy-tab"
          role="tab"
          [attr.aria-selected]="activeTab() === tab.value"
          [class.active]="activeTab() === tab.value"
          (click)="tabChange.emit(tab.value)">
          @if (tab.icon) {
            <span
              class="tidy-tab-icon"
              [style.-webkit-mask-image]="'url(' + tab.icon + ')'"
              [style.mask-image]="'url(' + tab.icon + ')'"></span>
          }
          {{ tab.label }}
        </div>
      }
    </div>
    @if (showRightArrow()) {
      <button class="tidy-tabs-arrow tidy-tabs-arrow-right" (click)="scrollRight()" aria-label="Scroll tabs right">
        <span
          class="tidy-tabs-arrow-icon"
          [style.-webkit-mask-image]="'url(assets/svg/chevron-forward-outline.svg)'"
          [style.mask-image]="'url(assets/svg/chevron-forward-outline.svg)'"></span>
      </button>
    }
  `
})
export class TabsComponent implements AfterViewInit, OnDestroy {
  tabs = input.required<{ value: string; label: string; icon?: string }[]>();
  activeTab = input.required<string>();
  tabChange = output<string>();

  private readonly ngZone = inject(NgZone);
  readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

  readonly showLeftArrow = signal(false);
  readonly showRightArrow = signal(false);

  private resizeObserver: ResizeObserver | null = null;
  private scrollListener: (() => void) | null = null;
  private readonly SCROLL_AMOUNT = 150;

  ngAfterViewInit(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) {return;}

    this.ngZone.runOutsideAngular(() => {
      this.scrollListener = () => this.ngZone.run(() => this.updateArrows());
      container.addEventListener('scroll', this.scrollListener, { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.ngZone.run(() => this.updateArrows()));
      this.resizeObserver.observe(container);
    });

    setTimeout(() => this.updateArrows(), 0);
  }

  scrollLeft(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) {return;}
    container.scrollBy({ left: -this.SCROLL_AMOUNT, behavior: 'smooth' });
  }

  scrollRight(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) {return;}
    container.scrollBy({ left: this.SCROLL_AMOUNT, behavior: 'smooth' });
  }

  private updateArrows(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (!container) {return;}
    const { scrollLeft, scrollWidth, clientWidth } = container;
    this.showLeftArrow.set(scrollLeft > 0);
    this.showRightArrow.set(scrollLeft + clientWidth < scrollWidth - 1);
  }

  ngOnDestroy(): void {
    const container = this.scrollContainer()?.nativeElement;
    if (container && this.scrollListener) {
      container.removeEventListener('scroll', this.scrollListener);
    }
    this.resizeObserver?.disconnect();
  }
}
